import React, { useState } from 'react';
import {
    Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, ListItemText, Menu, MenuItem,
    Tooltip, Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useOvladaniProbiha, useOvladejKanalMutation, useSmazKanalMutation } from '../../api/JsdhNotifikaceService/service/IotZarizeniMutations';
import {
    RUCNI_AKCE_PRO_TYP, pulsPovolen, type IotKanal, type IotZarizeni, type KatalogModel, type StavKanalu,
} from '../../api/JsdhNotifikaceService/service/IotZarizeniService';
import PravidloDialog, { castiPravidla, vetaPravidla } from './PravidloDialog';
import RucniOvladaniDialog, { parametrRucni } from './RucniOvladaniDialog';
import HistoriePrikazuDialog from './HistoriePrikazuDialog';
import { stavSpojeni, stitekSchvaleniSpravcem, zobrazeniStavuVystupu, type StavSpojeni } from './iotStav';

const BEZ_STAVU = stavSpojeni(null);

/** Položky menu Ovládat: Zastavit je samostatné tlačítko, Puls jen u modelu se suchým kontaktem nebo u neznámého modelu. */
export const polozkyOvladani = (kanal: IotKanal, model: KatalogModel | undefined) =>
    RUCNI_AKCE_PRO_TYP[kanal.typ].filter(({ akce }) => akce !== 'COVER_STOP' && (akce !== 'PULS' || pulsPovolen(model)));

const KanalKarta: React.FC<{
    zarizeni: IotZarizeni; kanal: IotKanal; stav?: StavKanalu; model?: KatalogModel; spojeni?: StavSpojeni; posledniKontaktMs?: number | null;
    /**
     * Karta se ukazuje samostatně, bez hlavičky karty zařízení. Jen tehdy nese upozornění na schválení správcem;
     * v kartě zařízení je upozornění jednou v hlavičce, ne u každého výstupu (E4).
     */
    samostatna?: boolean;
}> = ({ zarizeni, kanal, stav, model, spojeni = BEZ_STAVU, posledniKontaktMs, samostatna = false }) => {
    // Vlastni mutace na kanal (review L5): cekani jednoho kanalu neblokuje ostatni.
    const ovladej = useOvladejKanalMutation();
    // Zastavit ma vlastni mutaci, aby slo roletu zastavit i behem cekani na predchozi prikaz.
    const zastavit = useOvladejKanalMutation();
    // Probíhající příkaz se čte z cache mutací: přežije sbalení karty i přepnutí záložky (review K1-K5, bod 1).
    const odesila = useOvladaniProbiha(zarizeni.id, kanal.id) || ovladej.isPending;
    const smazKanal = useSmazKanalMutation();
    const [pravidlo, setPravidlo] = useState(false);
    const [historie, setHistorie] = useState(false);
    const [akceKPotvrzeni, setAkceKPotvrzeni] = useState<string | null>(null);
    const [odebrat, setOdebrat] = useState(false);
    const [menuOvladat, setMenuOvladat] = useState<HTMLElement | null>(null);
    const [menuDalsi, setMenuDalsi] = useState<HTMLElement | null>(null);

    const zobrazeni = zobrazeniStavuVystupu(kanal.typ, stav, spojeni.druh);
    // Jen upozornění: ruční ovládání ani pravidlo neblokuje, případné odmítnutí hlásí server pro zařízení.
    const schvaleni = samostatna ? stitekSchvaleniSpravcem(zarizeni.stavSchvaleni) : null;
    const polozky = polozkyOvladani(kanal, model);
    const pravidloCasti = castiPravidla(kanal.typ, kanal.pravidlo);
    const pravidloSede = !pravidloCasti || pravidloCasti.vypnuto || !kanal.aktivni;
    const idMenuOvladat = `kanal-${kanal.id}-menu-ovladat`;
    const idMenuDalsi = `kanal-${kanal.id}-menu-dalsi`;

    const posli = (akce: string, hodnota: number | null) => {
        const par = parametrRucni(akce, kanal.typ);
        ovladej.mutate(
            { id: zarizeni.id, kanalId: kanal.id, akce, ...(par && hodnota != null ? { [par.pole]: hodnota } : {}) },
            { onSettled: () => setAkceKPotvrzeni(null) },
        );
    };

    return (
        <Card variant="outlined" data-testid={'kanal-' + kanal.id}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }} color={kanal.aktivni ? 'text.primary' : 'text.disabled'}>{kanal.nazev}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', flexGrow: 1 }}>
                        <Typography variant="body2" color={zobrazeni.sede || !kanal.aktivni ? 'text.disabled' : 'text.primary'} data-testid={'kanal-stav-' + kanal.id}>
                            {zobrazeni.text}
                        </Typography>
                        {zobrazeni.mereni && <Typography variant="caption" color="text.secondary">{zobrazeni.mereni}</Typography>}
                        {zobrazeni.chyba && <Chip size="small" color="error" label={zobrazeni.chyba} />}
                        {schvaleni && (
                            <Tooltip title={schvaleni.tooltip ?? ''}>
                                <Chip size="small" color={schvaleni.barva} variant={schvaleni.variant} label={schvaleni.text} tabIndex={0}
                                    data-testid={'kanal-schvaleni-' + kanal.id} />
                            </Tooltip>
                        )}
                        {!kanal.aktivni && <Chip size="small" variant="outlined" label="Neaktivní" />}
                    </Box>
                    {kanal.aktivni && (
                        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon />} disabled={odesila}
                            aria-haspopup="menu" aria-expanded={!!menuOvladat} aria-controls={menuOvladat ? idMenuOvladat : undefined}
                            onClick={(e) => setMenuOvladat(e.currentTarget)}>
                            {odesila ? 'Odesílám…' : 'Ovládat'}
                        </Button>
                    )}
                    {kanal.aktivni && kanal.typ === 'cover' && (
                        <Button size="small" variant="outlined" disabled={zastavit.isPending}
                            onClick={() => zastavit.mutate({ id: zarizeni.id, kanalId: kanal.id, akce: 'COVER_STOP' })}>Zastavit</Button>
                    )}
                    <IconButton size="small" aria-label={`Další akce výstupu ${kanal.nazev}`} aria-haspopup="menu" aria-expanded={!!menuDalsi}
                        aria-controls={menuDalsi ? idMenuDalsi : undefined} onClick={(e) => setMenuDalsi(e.currentTarget)}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexGrow: 1, flexWrap: 'wrap' }} data-testid={'kanal-pravidlo-' + kanal.id}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} color={pravidloCasti?.vypnuto || !kanal.aktivni ? 'text.secondary' : 'text.primary'}>
                            Při poplachu
                        </Typography>
                        <Typography variant="body2" color={pravidloSede ? 'text.secondary' : 'text.primary'}>
                            {pravidloCasti
                                ? `${vetaPravidla(pravidloCasti)}${pravidloCasti.test ? ` ${pravidloCasti.test}` : ''}${pravidloCasti.vypnuto ? ' · vypnuto' : ''}`
                                : 'nenastaveno'}
                        </Typography>
                    </Box>
                    <Button size="small" onClick={() => setPravidlo(true)}>{kanal.pravidlo ? 'Změnit' : 'Nastavit'}</Button>
                </Box>
            </CardContent>

            <Menu id={idMenuOvladat} anchorEl={menuOvladat} open={!!menuOvladat} onClose={() => setMenuOvladat(null)}>
                {polozky.map(({ akce, label }) => (
                    <MenuItem key={akce} onClick={() => { setMenuOvladat(null); setAkceKPotvrzeni(akce); }}>{label}</MenuItem>
                ))}
            </Menu>
            <Menu id={idMenuDalsi} anchorEl={menuDalsi} open={!!menuDalsi} onClose={() => setMenuDalsi(null)}>
                <MenuItem onClick={() => { setMenuDalsi(null); setHistorie(true); }}><ListItemText>Historie příkazů</ListItemText></MenuItem>
                <Divider />
                <MenuItem sx={{ color: 'error.main' }} onClick={() => { setMenuDalsi(null); setOdebrat(true); }}><ListItemText>Odebrat výstup</ListItemText></MenuItem>
            </Menu>

            {akceKPotvrzeni && (
                <RucniOvladaniDialog zarizeni={zarizeni} kanal={kanal} akce={akceKPotvrzeni} spojeni={spojeni} posledniKontaktMs={posledniKontaktMs}
                    odesila={odesila} onPotvrdit={(h) => posli(akceKPotvrzeni, h)} onClose={() => setAkceKPotvrzeni(null)} />
            )}
            {historie && <HistoriePrikazuDialog zarizeniId={zarizeni.id} kanal={kanal} onClose={() => setHistorie(false)} />}
            {pravidlo && <PravidloDialog zarizeniId={zarizeni.id} kanal={kanal} model={model} onClose={() => setPravidlo(false)} />}
            <Dialog open={odebrat} onClose={() => setOdebrat(false)}>
                <DialogTitle>Odebrat výstup?</DialogTitle>
                <DialogContent><Typography variant="body2">Výstup {kanal.nazev} se odebere i s pravidlem pro poplach.</Typography></DialogContent>
                <DialogActions>
                    <Button variant="contained" autoFocus onClick={() => setOdebrat(false)}>Zrušit</Button>
                    <Button color="error" disabled={smazKanal.isPending}
                        onClick={() => smazKanal.mutate({ id: zarizeni.id, kanalId: kanal.id }, { onSettled: () => setOdebrat(false) })}>Odebrat</Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default KanalKarta;
